/**
 * utils/superadmin.ts
 *
 * Jediné místo kde se definuje, kdo je superadmin.
 *
 * Pravidla:
 *  1. jakub@vicha.net je VŽDY superadmin (permanentní, nelze odebrat přes UI).
 *  2. Kdokoliv s role === "superadmin" v Firestore /users/{uid} je taktéž superadmin.
 *
 * Používejte `useSuperAdmin()` hook v komponentách.
 * Pro one-off kontroly mimo React použijte `checkSuperAdmin(uid, email)`.
 */

import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

/** Email který je vždy superadmin, bez ohledu na Firestore */
export const PERMANENT_SUPER_ADMIN_EMAIL = "jakub@vicha.net";

/**
 * Asynchronní kontrola – lze volat kdekoliv mimo React (např. v event handlerech).
 * Vrátí true pokud:
 *  - email === PERMANENT_SUPER_ADMIN_EMAIL, NEBO
 *  - Firestore users/{uid}.role === "superadmin"
 */
export async function checkSuperAdmin(uid: string, email: string): Promise<boolean> {
  if (email === PERMANENT_SUPER_ADMIN_EMAIL) return true;
  try {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.exists() && snap.data()?.role === "superadmin";
  } catch {
    return false;
  }
}

/**
 * React hook – sleduje auth stav a vrátí aktuální isSuperAdmin.
 * Při odhlášení vrátí false.
 */
export function useSuperAdmin(): boolean {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) { setIsSuperAdmin(false); return; }
      const result = await checkSuperAdmin(user.uid, user.email || "");
      setIsSuperAdmin(result);
    });
    return unsubscribe;
  }, []);

  return isSuperAdmin;
}
